/**
 * Owner: Masa Bayu
 * File: author-integration-journey.ts
 * Date:Time: 2026-09-16 15:20
 * Description: T6-E7 vertical author journey — compose E1–E6 without QXW/payment authority.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { authorizeAuthorSurfaceAccess } from './author-account';
import { projectAuthorAnalyticsSlice } from './author-analytics';
import { applyCommerceEarningSettlement, projectEarningLedgerFromSaleRecords, } from './author-earnings';
import { evaluatePublishReadiness, } from './author-publish-readiness';
import { assertT6E1SecurityIntact, assertT6E2SecurityIntact, assertT6E3SecurityIntact, assertT6E4SecurityIntact, assertT6E5SecurityIntact, assertT6E6SecurityIntact, assertT6E7SecurityIntact, } from './author-security';
import { createStorefrontShopperPreview, upsertStorefrontConfig } from './author-storefront';
import { createAuthorWriterLaunchIntent } from './author-writer-launch';
import { assertTimestamp } from './guards';
import { createQxreadId } from './id';
import { deriveChromeMode } from './identity';
export const QXREAD_AUTHOR_JOURNEY_STATES = ['completed', 'soft_gated', 'denied'];
export const QXREAD_AUTHOR_JOURNEY_STEPS = [
    'surface_access',
    'writer_launch',
    'publish_readiness',
    'storefront',
    'sales',
    'earnings',
    'analytics',
];
function journeyStep(name, ok, detail) {
    return Object.freeze({ step: name, ok, detail });
}
/**
 * Compose the vertical T6 author platform journey (E1–E6). QXRead never hosts QXW or executes payment.
 */
export function runAuthorIntegrationJourney(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead author integration journey');
    assertT6E1SecurityIntact();
    assertT6E2SecurityIntact();
    assertT6E3SecurityIntact();
    assertT6E4SecurityIntact();
    assertT6E5SecurityIntact();
    assertT6E6SecurityIntact();
    assertT6E7SecurityIntact();
    const journeyId = input.journeyId ?? createQxreadId('qxrdajny');
    const now = input.now ?? new Date().toISOString();
    assertTimestamp(now, 'QXRead author journey now');
    const steps = [];
    const chromeMode = deriveChromeMode(input.account.roles);
    const finish = (partial) => Object.freeze({
        journeyId,
        state: partial.state,
        allowed: partial.allowed,
        reason: partial.reason,
        publicationId: input.publicationId,
        steps: Object.freeze(steps),
        chromeMode,
        chromeModeIsSecurityAuthority: false,
        hostsQxwMutation: false,
        mintsEntitlement: false,
        executesPayment: false,
        composesE1ThroughE6: true,
    });
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    steps.push(journeyStep('surface_access', access.allowed, access.allowed ? 'Author surface access granted' : access.reason));
    if (!access.allowed) {
        return finish({ state: 'denied', allowed: false, reason: access.reason });
    }
    const writer = createAuthorWriterLaunchIntent({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
        createdAt: now,
    });
    steps.push(journeyStep('writer_launch', writer.allowed, writer.allowed
        ? `Writer intent · executesAuthoring=${String(writer.intent.executesAuthoring)}`
        : writer.reason));
    const readiness = evaluatePublishReadiness(input.readiness);
    steps.push(journeyStep('publish_readiness', readiness.ready, readiness.ready ? 'Publish readiness satisfied' : `Missing: ${readiness.missing.join(', ')}`));
    if (!readiness.ready) {
        return finish({
            state: 'soft_gated',
            allowed: false,
            reason: 'Publish readiness incomplete',
        });
    }
    const storefront = upsertStorefrontConfig({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
        publicationId: input.publicationId,
        title: 'Journey Title',
        visibility: 'public_listed',
        sellerPriceMinorUnits: 1299,
        currency: 'USD',
        qxpPublicationRef: input.qxpPublicationRef ?? 'qxpref_demo',
        updatedAt: now,
    });
    if (!storefront.allowed) {
        steps.push(journeyStep('storefront', false, storefront.reason));
        return finish({ state: 'denied', allowed: false, reason: storefront.reason });
    }
    const preview = createStorefrontShopperPreview({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
        config: storefront.config,
    });
    steps.push(journeyStep('storefront', preview.allowed, preview.allowed
        ? `Shopper preview · grantsEntitlement=${String(preview.preview.grantsEntitlement)}`
        : preview.reason));
    if (!preview.allowed) {
        return finish({ state: 'denied', allowed: false, reason: preview.reason });
    }
    const sales = input.saleRecords ?? [];
    if (sales.length === 0) {
        steps.push(journeyStep('sales', true, 'Skipped — no SaleRecord input (Commerce confirmation separate)'));
        steps.push(journeyStep('earnings', true, 'Skipped — no SaleRecord input'));
        steps.push(journeyStep('analytics', true, 'Skipped — no SaleRecord input'));
        return finish({
            state: 'completed',
            allowed: true,
            reason: 'E1–E4 vertical complete · sales chain optional',
        });
    }
    const ownedSales = sales.filter((record) => record.authorAccountId === input.authorAccount.authorAccountId);
    steps.push(journeyStep('sales', ownedSales.length > 0, ownedSales.length > 0
        ? `SaleRecords projected · count=${String(ownedSales.length)}`
        : 'No author-owned SaleRecords'));
    const ledgerPending = projectEarningLedgerFromSaleRecords({
        authorAccount: input.authorAccount,
        saleRecords: ownedSales,
        updatedAt: now,
    });
    const ledger = applyCommerceEarningSettlement({ ledgerEntries: ledgerPending, settledAt: now });
    steps.push(journeyStep('earnings', ledger.length > 0, ledger.length > 0 ? `EarningLedger · entries=${String(ledger.length)}` : 'No ledger lines'));
    const slice = projectAuthorAnalyticsSlice({
        authorAccount: input.authorAccount,
        saleRecords: ownedSales,
        periodLabel: 'journey',
        projectedAt: now,
    });
    steps.push(journeyStep('analytics', slice.publicationRollups.length > 0, `Analytics slice · rollups=${String(slice.publicationRollups.length)} · includesRawReaderTelemetry=false`));
    const salesOk = ownedSales.length > 0 && ledger.length > 0 && slice.publicationRollups.length > 0;
    return finish({
        state: salesOk ? 'completed' : 'soft_gated',
        allowed: salesOk,
        reason: salesOk ? 'E1–E6 vertical composed' : 'Sales chain incomplete',
    });
}
//# sourceMappingURL=author-integration-journey.js.map